// thrashLedger - evictions, reloads and tile unload/reload churn, counted
// as they happen (round 10b, 2026-10-14).
//
// WHY. Tom's real-GPU cold settles of SpaceX are bimodal: about 30 s or
// about 93-105 s. The slow runs ask again for more of the same pack and
// fragment URLs (1,345-1,585 repeated URLs against 1,116-1,201), all of it
// answered by the browser's disk cache - so the cost is parse and
// re-materialise work, the signature of an evict/reload loop. This ledger
// is what says whether such a loop is there, and where:
// window.__sl.perfSnapshot().thrash (summary) and window.__sl.thrash()
// (per tile and per hash detail).
//
// CHEAP ENOUGH TO LEAVE ON. Every call is O(1): counters, one Map entry per
// tile (about 1.4k on SpaceX), a Set of hashes currently evicted and a Map
// of hashes reloaded at least once (both bounded by the fragments in the
// model). Nothing here walks a pool, a tile or the scene; sorting for the
// top-N lists happens only when someone asks for a summary.
//
// WORDS.
//   eviction       a library (one fragment's geometry, every LOD) or an
//                  instanced entry dropped by the LRU in GeometryCache.
//   reload         a library put back into the cache after it was evicted -
//                  by a fetch for it, or prefilled by a pack another
//                  fragment needed. Adding a missing LOD to a library that
//                  is still cached is NOT a reload.
//   tile unload    anything that takes a tile's installed geometry away:
//                  a full _unload, a LOD swap / forced retire swap (which
//                  rebuilds the tile; it is counted as an unload AND a
//                  load), a watchdog reset, a stale run thrown away.
//   tile load      a tile's geometry installed: first materialise, a swap
//                  rebuild, a revive of deferred groups.
//   tile reload    a tile load that happens after that tile's first
//                  unload. A tile reloaded over and over is thrash.
//   hide           the frustum soft-hide (slots parked, nothing freed);
//                  counted per tile but not an unload.
//
// Main thread only, one module-level ledger like fetchLedger; reset per
// model load by StreamingLoader.

const TOP_N = 20;
export const BUCKET_MS = 5000;
const MAX_BUCKETS = 360;     // 30 min of 5 s buckets; older ones fold into `dropped`

function clockNow() {
  return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
}

function emptyBucket() {
  return { evictions: 0, reloads: 0, tileLoads: 0, tileUnloads: 0 };
}

export class ThrashLedger {
  constructor({ now = clockNow } = {}) {
    this._now = now;
    this.reset();
  }

  reset() {
    this.startedAt = this._now();
    this.library = {
      evictions: 0,
      evictedBytes: 0,
      byReason: { entryCap: { count: 0, bytes: 0 }, byteCap: { count: 0, bytes: 0 } },
      reloads: 0,
      reloadsBy: { fetch: 0, prefill: 0 },
    };
    this.instanced = { evictions: 0 };
    this.tileTotals = { loads: 0, unloads: 0, reloads: 0, hides: 0, byReason: {} };
    this._gone = new Set();          // hashes evicted and not (yet) back
    this._reloadsByHash = new Map(); // hash -> times reloaded
    this._tiles = new Map();         // tileKey -> per-tile record
    this._buckets = [];              // dense, index 0 = bucket _firstBucket
    this._firstBucket = 0;
    this._dropped = emptyBucket();   // buckets that fell off the front
  }

  // ---- time series -------------------------------------------------------

  _bucket() {
    const idx = Math.max(0, Math.floor((this._now() - this.startedAt) / BUCKET_MS));
    let last = this._firstBucket + this._buckets.length - 1;
    if (idx > last + MAX_BUCKETS) {
      // A long quiet gap: everything held is older than the window.
      this._fold(this._buckets.length);
      this._firstBucket = idx;
      last = idx - 1;
    }
    while (last < idx) { this._buckets.push(emptyBucket()); last++; }
    if (this._buckets.length > MAX_BUCKETS) this._fold(this._buckets.length - MAX_BUCKETS);
    // An event stamped before the window's first bucket (cannot happen with
    // a monotonic clock) lands in the oldest one rather than being lost.
    return this._buckets[Math.max(0, idx - this._firstBucket)];
  }

  _fold(n) {
    for (const b of this._buckets.splice(0, n)) {
      this._dropped.evictions += b.evictions;
      this._dropped.reloads += b.reloads;
      this._dropped.tileLoads += b.tileLoads;
      this._dropped.tileUnloads += b.tileUnloads;
    }
    this._firstBucket += n;
  }

  // ---- library / instanced ----------------------------------------------

  // `reason`: 'entryCap' (over maxLibraryEntries) or 'byteCap' (over
  // maxLibraryBytes).
  libraryEvicted(hash, bytes = 0, reason = 'byteCap') {
    const r = this.library.byReason[reason] || (this.library.byReason[reason] = { count: 0, bytes: 0 });
    r.count++;
    r.bytes += bytes || 0;
    this.library.evictions++;
    this.library.evictedBytes += bytes || 0;
    this._gone.add(hash);
    this._bucket().evictions++;
  }

  // A library entry was created (not merely extended) for `hash`.
  // `way`: 'fetch' or 'prefill'. Returns true when it was a reload.
  libraryPut(hash, way = 'fetch') {
    if (!this._gone.has(hash)) return false;
    this._gone.delete(hash);
    this.library.reloads++;
    this.library.reloadsBy[way] = (this.library.reloadsBy[way] || 0) + 1;
    this._reloadsByHash.set(hash, (this._reloadsByHash.get(hash) || 0) + 1);
    this._bucket().reloads++;
    return true;
  }

  instancedEvicted(n = 1) {
    if (!(n > 0)) return;
    this.instanced.evictions += n;
    this._bucket().evictions += n;
  }

  // ---- tiles -------------------------------------------------------------

  _tile(key) {
    let t = this._tiles.get(key);
    if (!t) {
      t = { loads: 0, unloads: 0, reloads: 0, hides: 0, lastUnloadReason: null, lastUnloadAt: null, reasons: {} };
      this._tiles.set(key, t);
    }
    return t;
  }

  // `reason` names what took the geometry: 'retire:budget', 'retire:cone',
  // 'lod', 'coarsen', 'frustum', 'outliers', 'poolGone', 'watchdog', ...
  tileUnloaded(key, reason = 'unknown') {
    const t = this._tile(key);
    t.unloads++;
    t.lastUnloadReason = reason;
    t.lastUnloadAt = Math.round(this._now() - this.startedAt);
    t.reasons[reason] = (t.reasons[reason] || 0) + 1;
    this.tileTotals.unloads++;
    this.tileTotals.byReason[reason] = (this.tileTotals.byReason[reason] || 0) + 1;
    this._bucket().tileUnloads++;
  }

  // `how`: 'load' (first materialise or after an unload), 'swap', 'revive'.
  // A load after this tile's first unload is a reload.
  tileLoaded(key, how = 'load') {
    const t = this._tile(key);
    t.loads++;
    this.tileTotals.loads++;
    const b = this._bucket();
    b.tileLoads++;
    if (t.unloads > 0) {
      t.reloads++;
      this.tileTotals.reloads++;
      if (how) t.lastReloadHow = how;
    }
  }

  tileHidden(key) {
    this._tile(key).hides++;
    this.tileTotals.hides++;
  }

  // ---- read-outs ---------------------------------------------------------

  _topTiles(n) {
    const rows = [];
    this._tiles.forEach((t, key) => { if (t.reloads > 0) rows.push([key, t]); });
    rows.sort((a, b) => (b[1].reloads - a[1].reloads) || (b[1].unloads - a[1].unloads));
    return rows.slice(0, n).map(([key, t]) => ({
      tile: key, reloads: t.reloads, unloads: t.unloads, loads: t.loads, hides: t.hides,
      lastUnloadReason: t.lastUnloadReason, reasons: { ...t.reasons },
    }));
  }

  _topHashes(n) {
    const rows = [];
    this._reloadsByHash.forEach((count, hash) => rows.push([hash, count]));
    rows.sort((a, b) => b[1] - a[1]);
    return rows.slice(0, n);
  }

  series() {
    return {
      bucketMs: BUCKET_MS,
      // Seconds since the ledger was reset at which buckets[0] starts.
      firstAtS: (this._firstBucket * BUCKET_MS) / 1000,
      dropped: { ...this._dropped },
      buckets: this._buckets.map((b, i) => ({ t: ((this._firstBucket + i) * BUCKET_MS) / 1000, ...b })),
    };
  }

  summary(topN = TOP_N) {
    let unloaded = 0;
    let reloaded = 0;
    let reloadedMoreThanOnce = 0;
    let maxReloads = 0;
    this._tiles.forEach((t) => {
      if (t.unloads > 0) unloaded++;
      if (t.reloads > 0) reloaded++;
      if (t.reloads > 1) reloadedMoreThanOnce++;
      if (t.reloads > maxReloads) maxReloads = t.reloads;
    });
    let hashesReloadedMoreThanOnce = 0;
    this._reloadsByHash.forEach((c) => { if (c > 1) hashesReloadedMoreThanOnce++; });
    return {
      sinceS: Math.round((this._now() - this.startedAt) / 100) / 10,
      library: {
        evictions: this.library.evictions,
        evictedBytes: this.library.evictedBytes,
        byReason: JSON.parse(JSON.stringify(this.library.byReason)),
        reloads: this.library.reloads,
        reloadsBy: { ...this.library.reloadsBy },
        reloadedHashes: this._reloadsByHash.size,
        hashesReloadedMoreThanOnce,
        evictedNow: this._gone.size,
        topReloaded: this._topHashes(topN),
      },
      instanced: { ...this.instanced },
      tiles: {
        loads: this.tileTotals.loads,
        unloads: this.tileTotals.unloads,
        reloads: this.tileTotals.reloads,
        hides: this.tileTotals.hides,
        byReason: { ...this.tileTotals.byReason },
        tracked: this._tiles.size,
        unloaded,
        reloaded,
        reloadedMoreThanOnce,
        maxReloads,
        top: this._topTiles(topN),
      },
      series: this.series(),
    };
  }

  // Everything per tile and per hash, for window.__sl.thrash().
  detail(limit = Infinity) {
    const tiles = {};
    this._tiles.forEach((t, key) => { tiles[key] = { ...t, reasons: { ...t.reasons } }; });
    return {
      summary: this.summary(),
      tiles,
      reloadedHashes: this._topHashes(limit),
    };
  }
}

const thrashLedger = new ThrashLedger();
export default thrashLedger;
